import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Button, Checkbox, Dialog, Input, Select } from '@artosera/ui';
import {
  CHAMPS_PAR_NATURE,
  NATURES_DEPOT,
  champManquant,
  deposerLot,
  telechargerGabarit,
  type ChampLot,
  type LotReprise,
  type NatureDepot,
} from '../../../lib/reprise.js';
import formStyles from '../../../styles/form.module.css';
import { messageReprise } from './messages.js';
import styles from './Reprise.module.css';

const ACCEPTE: Record<'tableur' | 'archive', string> = {
  tableur: '.csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  archive: '.zip,application/zip',
};

/**
 * « Nouveau lot » : la nature, puis ses fichiers (le tableur principal, les
 * annexes de la nature) et ses options. Le dépôt n'écrit rien chez la galerie ;
 * le lot arrive à l'état « déposé », prêt pour l'essai.
 */
export function DepotDialog({
  idGalerie,
  ouvert,
  onFermer,
  onDepose,
}: {
  idGalerie: string;
  ouvert: boolean;
  onFermer: () => void;
  onDepose: (lot: LotReprise) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const [nature, setNature] = useState<NatureDepot>('oeuvres');
  const [fichiers, setFichiers] = useState<Partial<Record<ChampLot, File>>>({});
  const [confidentiels, setConfidentiels] = useState(false);
  const [motifInventaire, setMotifInventaire] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  // Une clé qui change vide les champs fichier natifs (ils ne se contrôlent pas).
  const [generation, setGeneration] = useState(0);

  const depot = { nature, fichiers, confidentiels, motifInventaire };
  const manquant = champManquant(depot);

  const reinitialiser = (): void => {
    setFichiers({});
    setConfidentiels(false);
    setMotifInventaire('');
    setErreur(null);
    setGeneration((courante) => courante + 1);
  };

  const mutation = useMutation({
    mutationFn: () => deposerLot(idGalerie, depot),
    onSuccess: (lot) => {
      reinitialiser();
      onDepose(lot);
    },
    onError: (cause) => setErreur(messageReprise(t, cause)),
  });

  const telecharger = (feuille?: 'accrochages'): void => {
    setErreur(null);
    telechargerGabarit(idGalerie, { nature, ...(feuille ? { feuille } : {}) }).catch(
      (cause: unknown) => setErreur(messageReprise(t, cause)),
    );
  };

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (manquant === null) mutation.mutate();
  };

  return (
    <Dialog
      open={ouvert}
      onOpenChange={(estOuvert) => {
        if (!estOuvert) onFermer();
      }}
      title={t('reprise.depot.titre')}
      description={t('reprise.depot.texte')}
      closeLabel={t('reprise.depot.annuler')}
      footer={
        <>
          <Button variant="ghost" onClick={onFermer}>
            {t('reprise.depot.annuler')}
          </Button>
          <Button
            loading={mutation.isPending}
            disabled={manquant !== null}
            onClick={() => mutation.mutate()}
          >
            {t('reprise.depot.deposer')}
          </Button>
        </>
      }
    >
      <form className={styles.pile} onSubmit={soumettre} noValidate>
        <div className={formStyles.champ}>
          <span>{t('reprise.depot.nature')}</span>
          <Select
            aria-label={t('reprise.depot.nature')}
            value={nature}
            onValueChange={(valeur) => {
              setNature(valeur as NatureDepot);
              reinitialiser();
            }}
            options={NATURES_DEPOT.map((n) => ({ value: n, label: t(`reprise.natures.${n}`) }))}
          />
        </div>

        <div className={styles.actions}>
          <Button variant="ghost" size="sm" onClick={() => telecharger()}>
            {t('reprise.depot.gabarit')}
          </Button>
          {nature === 'expositions' ? (
            <Button variant="ghost" size="sm" onClick={() => telecharger('accrochages')}>
              {t('reprise.depot.gabaritAccrochages')}
            </Button>
          ) : null}
        </div>

        {CHAMPS_PAR_NATURE[nature].map(({ champ, requis, genre }) => (
          <label key={`${nature}-${champ}-${String(generation)}`} className={styles.fichier}>
            <span>
              {t(`reprise.depot.champs.${champ}`)}
              {requis ? '' : ` (${t('reprise.depot.facultatif')})`}
            </span>
            <input
              type="file"
              accept={ACCEPTE[genre]}
              onChange={(e) => {
                const choisi = e.target.files?.[0];
                setFichiers((courants) => {
                  const suite = { ...courants };
                  if (choisi) suite[champ] = choisi;
                  else delete suite[champ];
                  return suite;
                });
              }}
            />
          </label>
        ))}

        {nature === 'documents' ? (
          <div className={formStyles.champ}>
            <Checkbox
              id="depot-confidentiels"
              label={t('reprise.depot.confidentiels')}
              checked={confidentiels}
              onCheckedChange={(coche) => setConfidentiels(coche === true)}
            />
            <span className={formStyles.aide}>{t('reprise.depot.confidentielsAide')}</span>
          </div>
        ) : null}

        {nature === 'oeuvres' ? (
          <div className={formStyles.champ}>
            <label htmlFor="depot-motif-inventaire">{t('reprise.depot.motifInventaire')}</label>
            <Input
              id="depot-motif-inventaire"
              value={motifInventaire}
              maxLength={60}
              onChange={(e) => setMotifInventaire(e.target.value)}
            />
            <span className={formStyles.aide}>{t('reprise.depot.motifInventaireAide')}</span>
          </div>
        ) : null}

        {manquant !== null ? (
          <span className={formStyles.aide}>
            {t('reprise.depot.manquant', { champ: t(`reprise.depot.champs.${manquant}`) })}
          </span>
        ) : null}
        {erreur ? (
          <p className={styles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
